import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  cancelMeeting,
  createMeeting,
  getMeetingById,
  getMeetingsFromToday,
  getOccupiedSlotsFromToday,
  updateMeeting,
} from '@/lib/api/meeting';

import { queryKeys } from './query-keys-factory';

/**
 * Query Options Factories
 */
export const meetingQueries = {
  /**
   * Get all meetings from today onwards (for availability)
   */
  fromToday: () =>
    queryOptions({
      queryKey: queryKeys.meetings.list({ fromToday: true }),
      queryFn: getMeetingsFromToday,
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: false,
    }),

  /**
   * Get only occupied time slots from today onwards (optimized)
   */
  occupiedSlots: () =>
    queryOptions({
      queryKey: queryKeys.meetings.list({ occupiedSlots: true }),
      queryFn: getOccupiedSlotsFromToday,
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: false,
    }),

  /**
   * Get meeting by ID
   */
  byId: (id: string) =>
    queryOptions({
      queryKey: queryKeys.meetings.detail(id),
      queryFn: () => getMeetingById(id),
      staleTime: 1000 * 60 * 10, // 10 minutes
      enabled: !!id,
      retry: false,
    }),
};

/**
 * Query Hooks
 */

/**
 * Hook to fetch meetings from today onwards
 */
export const useGetMeetingsFromToday = () => useQuery(meetingQueries.fromToday());

/**
 * Hook to fetch only occupied time slots from today onwards (optimized)
 */
export const useGetOccupiedSlotsFromToday = () => useQuery(meetingQueries.occupiedSlots());

/**
 * Hook to fetch meeting by ID
 */
export const useGetMeetingById = (id: string) => useQuery(meetingQueries.byId(id));

/**
 * Mutation Hooks
 */

type CreateMeetingPayload = {
  prestations: Array<{
    service_id: string;
    product_id?: string;
  }>;
  start_time: Date | string;
  send_notification?: boolean;
  client: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
};

type UpdateMeetingPayload = {
  id: string;
  token: string;
  prestations: Array<{
    service_id: string;
    product_id?: string;
  }>;
  start_time: Date | string;
  send_notification?: boolean;
};

type CancelMeetingPayload = {
  id: string;
  token: string;
  send_notification?: boolean;
};

/**
 * Hook to create a new meeting
 */
export const useCreateMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateMeetingPayload) => createMeeting(payload),
    onSuccess: (response) => {
      if (response.conflicts && response.conflicts.length > 0) {
        // Don't invalidate cache on conflicts, user needs to choose a different time
        toast.error(
          'Un rendez-vous existe déjà pour cette période. Veuillez choisir un autre horaire.',
        );
        return;
      }

      if (response.success && response.data) {
        toast.success('Rendez-vous créé avec succès !');

        // Invalidate meetings list to refresh availability
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings.lists() });
      } else {
        console.warn('⚠️ Response does not have success=true or data:', response);
      }
    },
    onError: (error: Error) => {
      console.error('❌ Error creating meeting:', error);
      toast.error(error.message || 'Une erreur est survenue lors de la création du rendez-vous');
    },
  });
};

/**
 * Hook to update an existing meeting
 */
export const useUpdateMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, token, ...payload }: UpdateMeetingPayload) =>
      updateMeeting(id, { ...payload, token }),
    onSuccess: (response, variables) => {
      if (response.conflicts && response.conflicts.length > 0) {
        toast.error(
          'Un rendez-vous existe déjà pour cette période. Veuillez choisir un autre horaire.',
        );
        return;
      }

      if (response.success && response.data) {
        toast.success('Rendez-vous modifié avec succès !');

        // Invalidate meetings list and specific meeting
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings.lists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings.detail(variables.id) });
      }
    },
    onError: (error: Error) => {
      console.error('Error updating meeting:', error);
      toast.error(
        error.message || 'Une erreur est survenue lors de la modification du rendez-vous',
      );
    },
  });
};

/**
 * Hook to cancel a meeting
 */
export const useCancelMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, token, send_notification = true }: CancelMeetingPayload) =>
      cancelMeeting(id, { send_notification, token }),
    onSuccess: (response, variables) => {
      if (response.success) {
        toast.success('Rendez-vous annulé avec succès');

        // Invalidate meetings list and specific meeting
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings.lists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings.detail(variables.id) });
      }
    },
    onError: (error: Error) => {
      console.error('Error cancelling meeting:', error);
      toast.error(error.message || "Une erreur est survenue lors de l'annulation du rendez-vous");
    },
  });
};
